import React, { useState, useEffect, useRef } from "react";
import Axios from "axios";
import { GetCookie } from "../../exports/CookieManagement";
import SwiftModal from "../CustomComponents/SwiftModal/SwiftModal";
import CustomIconButton from "../Body/CustomChartComponents/CustomIconButton/CustomIconButton";
import CustomSelect from "../Body/CustomChartComponents/CustomSelect/CustomSelect";
import Pulse from "../Loader/Pulse";
import { Cross, Add, ReceiptEdit, Refresh } from "../CustomComponents/SwiftIcon/Icons";
import { COLOR_VARS } from "../../exports/ColorVars";
import fail from "../../assets/icons/fail.svg";
import "../../css/RaiseTicket/RaiseTicket.css";
import Badge from "../ui/badge";
import { getDeviceSize } from "../../exports/InteractiveUtils";
const REQUEST_BASE_URL = process.env.REACT_APP_REQUEST_BASE_URL;

function RaiseTicket() {
  const account_token = GetCookie("token");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    subject: "Select",
    description: "",
  });
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [ticketsLoading, setTicketsLoading] = useState(false);
  const [ticketsError, setTicketsError] = useState(null);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [conversationFiles, setConversationFiles] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [conversationLoading, setConversationLoading] = useState(false);
  const [conversationError, setConversationError] = useState("");
  const [conversations, setConversations] = useState([]);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [subjectOptions, setSubjectOptions] = useState([
    {
      value: "Select Category",
      text: "Select Category",
      requiresFiles: false,
    },
  ]);
  const [subjectsLoading, setSubjectsLoading] = useState(false);
  const messagesEndRef = useRef(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear files when subject changes
    if (name === "subject") {
      setUploadedFiles([]);
    }
  };

  const handleSubjectChange = (selectedText) => {
    // Find the option that matches the selected text
    const selectedOption = subjectOptions.find(
      (opt) => opt.text === selectedText,
    );
    const selectedValue = selectedOption ? selectedOption.value : selectedText;

    setFormData((prev) => ({
      ...prev,
      subject: selectedValue,
    }));

    setUploadedFiles([]);
  };

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = [];
    const errors = [];

    files.forEach((file) => {
      // Check file type
      const validTypes = [
        "image/jpeg",
        "image/jpg",
        "image/png",
        "application/pdf",
      ];
      if (!validTypes.includes(file.type)) {
        errors.push(
          `${file.name}: Only JPG, JPEG, PNG, and PDF files are allowed`,
        );
        return;
      }

      // Check file size (5MB = 5 * 1024 * 1024 bytes)
      const maxSize = 5 * 1024 * 1024;
      if (file.size > maxSize) {
        errors.push(`${file.name}: File size must be less than 5MB`);
        return;
      }

      validFiles.push(file);
    });

    if (errors.length > 0) {
      setSubmitStatus({
        type: "error",
        message: errors.join(", "),
      });
      return;
    }

    setUploadedFiles((prev) => [...prev, ...validFiles]);
    setSubmitStatus(null);
  };

  const removeFile = (index) => {
    setUploadedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const getCurrentSubject = () => {
    return (
      subjectOptions.find((opt) => opt.value === formData.subject) ||
      subjectOptions[0]
    );
  };

  const truncateFileName = (fileName, maxLength = 30) => {
    if (!fileName) return "";
    if (fileName.length <= maxLength) return fileName;
    const firstPart = fileName.substring(0, 10);
    const lastPart = fileName.substring(fileName.length - 10);
    return firstPart + "..." + lastPart;
  };

  // Convert text to HTML preserving spaces and line breaks
  const convertToHtml = (text) => {
    if (!text) return "";
    return text.replace(/ /g, "&nbsp;").replace(/\n/g, "<br>");
  };

  // Auto-scroll to bottom of messages
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const fetchSubjects = async () => {
    try {
      setSubjectsLoading(true);

      const response = await Axios.get(`${REQUEST_BASE_URL}/support/subjects`);

      if (response.data.error === false && response.data.data) {
        const dbSubjects = response.data.data.map((subject) => ({
          value: subject.topic,
          text: subject.topic,
          requiresFiles: true,
          uploadRequired: false,
          uploadText: "Upload supporting documents (optional)",
        }));

        const newOptions = [
          {
            value: "Select Category",
            text: "Select Category",
            requiresFiles: false,
            uploadRequired: false,
            uploadText: null,
          },
          ...dbSubjects,
        ];
        setSubjectOptions(newOptions);
      }
    } catch (err) {
      console.error("Error fetching subjects:", err);
    } finally {
      setSubjectsLoading(false);
    }
  };

  const fetchTickets = async () => {
    try {
      setTicketsLoading(true);
      setTicketsError(null);

      const response = await Axios.get(
        `${REQUEST_BASE_URL}/support/tickets?token=${account_token}`,
      );

      // console.log(" API Response:", response.data);
      // console.log(" Tickets data:", response.data.data);

      if (response.data.error) {
        throw new Error(response.data.message || "Failed to fetch tickets");
      }
      const ticketsData = response.data.data || [];
      setTickets(ticketsData);
      setTicketsLoading(false);
    } catch (err) {
      console.error("Error fetching tickets:", err);
      setTicketsError(err.message || "Failed to load tickets");
      setTicketsLoading(false);
    }
  };
  useEffect(() => {
    fetchTickets();
    fetchSubjects();
  }, []);

  // Auto-scroll when conversations change
  useEffect(() => {
    if (conversations.length > 0) {
      scrollToBottom();
    }
  }, [conversations]);

  const closeModal = () => {
    setIsModalOpen(false);
    setFormData({
      subject: "Select",
      description: "",
    });
    setUploadedFiles([]);
    setSubmitStatus(null);
  };

  const openTicketDetails = (ticket) => {
    console.log("Opening ticket details for:", ticket);
    setSelectedTicket(ticket);
    setIsDetailsModalOpen(true);
    loadConversations(ticket.ticket_id);
  };

  const closeDetailsModal = () => {
    setIsDetailsModalOpen(false);
    setSelectedTicket(null);
    setConversations([]);
    setNewMessage("");
    setConversationFiles([]);
    // Refresh tickets list to reflect new sorting when modal closes
    fetchTickets();
  };

  const loadConversations = async (ticketId) => {
    try {
      const response = await Axios.get(
        `${REQUEST_BASE_URL}/support-tickets/conversation/${ticketId}`,
        {
          headers: {
            Authorization: `Bearer ${account_token}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (response.data && response.data.conversations) {
        setConversations(response.data.conversations);
      } else {
        setConversations([]);
      }
    } catch (error) {
      console.error("Error loading conversations:", error);
      setConversations([]);
    }
  };

  const sendMessage = async () => {
    if (!newMessage || !selectedTicket) return;
    if (sendingMessage) return; // Prevent double sending

    try {
      setSendingMessage(true);
      let response;

      // Add message immediately to conversation for instant feedback
      const newMsg = {
        message: newMessage,
        sender: "user",
        senderName: "You",
        timestamp: new Date().toISOString(),
        fileUrls: conversationFiles.map((file) => ({
          url: URL.createObjectURL(file),
          originalName: file.name,
        })),
      };
      setConversations((prev) => [...prev, newMsg]);

      if (conversationFiles.length > 0) {
        const formData = new FormData();
        formData.append("message", newMessage);
        formData.append("sender", "user");
        formData.append("senderName", "You");

        conversationFiles.forEach((file) => {
          formData.append("files", file);
        });

        response = await Axios.post(
          `${REQUEST_BASE_URL}/support-tickets/conversation/${selectedTicket.ticket_id}/files`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${account_token}`,
              "Content-Type": "multipart/form-data",
            },
          },
        );
      } else {
        response = await Axios.post(
          `${REQUEST_BASE_URL}/support-tickets/conversation/${selectedTicket.ticket_id}`,
          {
            message: newMessage,
            sender: "user",
            senderName: "You",
          },
          {
            headers: {
              Authorization: `Bearer ${account_token}`,
              "Content-Type": "application/json",
            },
          },
        );
      }

      if (response.data.success) {
        setNewMessage("");
        setConversationFiles([]);
        // Optionally refresh conversation in background without blocking UI
        setTimeout(() => {
          loadConversations(selectedTicket.ticket_id);
        }, 100);
      }
    } catch (error) {
      console.error("Error sending message:", error);
      setConversationError("Failed to send message");
    } finally {
      setSendingMessage(false);
    }
  };

  const handleConversationFileChange = (e) => {
    const files = Array.from(e.target.files);
    setConversationFiles([...conversationFiles, ...files]);
  };

  const removeConversationFile = (index) => {
    const updatedFiles = conversationFiles.filter((_, i) => i !== index);
    setConversationFiles(updatedFiles);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.subject === "Select" || !formData.description.trim()) {
      setSubmitStatus({
        type: "error",
        message: "Please select an subject type and provide description.",
      });
      return;
    }

    const currentSubject = getCurrentSubject();
    if (currentSubject.uploadRequired && uploadedFiles.length === 0) {
      setSubmitStatus({
        type: "error",
        message: "Please upload required documents for this category.",
      });
      return;
    }

    setIsSubmitting(true);
    closeModal();
    setSubmitStatus(null);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append("subject", formData.subject);
      formDataToSend.append("category", formData.subject);
      formDataToSend.append("description", formData.description);
      formDataToSend.append("token", account_token);

      uploadedFiles.forEach((file) => {
        formDataToSend.append("files", file);
      });

      const response = await Axios.post(
        `${REQUEST_BASE_URL}/support/ticket`,
        formDataToSend,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      if (response.data.error) {
        throw new Error(response.data.message || "Failed to submit ticket");
      }

      setSubmitStatus({
        type: "success",
        message: `Ticket submitted successfully! Ticket ID: ${response.data.ticketId || "N/A"}`,
      });

      setFormData({
        subject: "Select",
        description: "",
      });
      setUploadedFiles([]);

      fetchTickets();
    } catch (error) {
      setSubmitStatus({
        type: "error",
        message:
          error.response?.data?.message ||
          error.message ||
          "Failed to submit ticket. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getStatusText = (status) => {
    switch (status?.toLowerCase()) {
      case "pending":
        return "open";
      case "accepted":
        return "Accepted";
      case "resolved":
        return "Resolved";
      case "rejected":
        return "Rejected";
      case "in_progress":
        return "In Progress";
      default:
        return status || "Unknown";
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString("en-IN", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return "Invalid Date";
    }
  };

  const size = getDeviceSize();

  return (
    <div className="support-tickets-container">
      <div className="tickets-header">
        <div className="header-content">
          <h2>Support Tickets</h2>
          <p>Manage your support tickets and get help from our team</p>
        </div>
        <CustomIconButton
          onClick={() => setIsModalOpen(true)}
          title="Raise New Ticket"
          disabled={false}
        />
      </div>
      <div className="tickets-list-header">
        <h3>Your Support Tickets ({tickets.length})</h3>
        <p onClick={fetchTickets} disabled={false} className="refresh">
          <Badge variant="outline" size="xxs">
            <Refresh size={13} color="currentColor" />{" "}
            Refresh
          </Badge>
        </p>
      </div>
      <div className="tickets-content">
        {ticketsLoading || isSubmitting ? (
          <div className="loading-container">
            <Pulse />
            {/* <p>Loading your Tickets...</p> */}
          </div>
        ) : ticketsError ? (
          <div>
            <p className="error-message">{ticketsError}</p>
            <CustomIconButton
              title={ticketsError ? "Fetching..." : "Try Again"}
              onClick={fetchTickets}
            />
          </div>
        ) : tickets.length === 0 ? (
          <div className="no-tickets">
            <ReceiptEdit size={50} color={COLOR_VARS["SWIFT_COLOR4"]} />
            <h3>No Support Tickets Found</h3>
            <p>You haven't submitted any support tickets yet.</p>
            <p>Click "Raise New Ticket" to get help from our support team.</p>
          </div>
        ) : (
          <div className="tickets-list">
            {tickets.map((ticket, index) => (
              <div
                key={ticket.ticket_id || index}
                className={`ticket-card ${ticket.status === "closed" ? "closed" : ""}`}
              >
                <div className="ticket-header">
                  <div className="ticket-id">
                    <div>{ticket.subject}</div>
                    <p>{formatDate(ticket.createdAt || ticket.created_at)}</p>
                    <p> #{ticket.ticket_id}</p>
                  </div>
                  <div
                    onClick={() => openTicketDetails(ticket)}
                    className="ticket-status"
                  >
                    {getStatusText(ticket.status)}
                  </div>
                </div>

                <div className="ticket-details">
                  <div className="ticket-info">
                    <div className="ticket-files-section">
                      {/* <p>{ticket.description}</p> */}
                      <p>{ticket.lastmessage}</p>
                      {ticket.filesUploaded > 0 && (
                        <p>
                          <strong>Files:</strong> file(s) attached
                        </p>
                      )}
                    </div>
                    {ticket.filesUploaded > 0 && (
                      <div className="ticket-files-uploaded-section">
                        {(() => {
                          // Priority: Latest conversation files > Original ticket files
                          const filesToShow =
                            ticket.latestConversationFiles &&
                            ticket.latestConversationFiles.length > 0
                              ? ticket.latestConversationFiles
                              : ticket.fileUrls;
                          if (filesToShow?.length > 0) {
                            console.log(
                              `   - File names:`,
                              filesToShow.map(
                                (f) => f.fileName || f.originalName,
                              ),
                            );
                          }

                          return (
                            filesToShow &&
                            filesToShow.length > 0 && (
                              <div className="latest-files-preview">
                                <div className="files-preview-container">
                                  {filesToShow.map((file, index) => (
                                    <div key={index} className="message-file">
                                      <a
                                        href={file.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                      >
                                        {truncateFileName(
                                          file.fileName ||
                                            file.originalName ||
                                            file.url?.split("/").pop() ||
                                            `File ${index + 1}`,
                                        )}
                                      </a>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )
                          );
                        })()}
                      </div>
                    )}
                    {ticket.category && (
                      <p>
                        <strong>Category:</strong> {ticket.category}
                      </p>
                    )}
                    {ticket.updated_at &&
                      ticket.updated_at !== ticket.created_at && (
                        <p>
                          <strong>Updated:</strong>{" "}
                          {formatDate(ticket.updated_at)}
                        </p>
                      )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {isModalOpen && (
        <SwiftModal closeModal={closeModal} top="2%">
          <div className="ticket-modal-content">
            <div className="modal-header">
              <p className="modal-title">Raise New Ticket</p>
              <div className="modal-close" onClick={closeModal}>
                <Cross size={24} color={COLOR_VARS["SWIFT_COLOR7"]} />
              </div>
            </div>

            <div className="modal-wrapper">
              <div className="modal-body">
                <form onSubmit={handleSubmit} className="modal-form">
                  <div className="form-group">
                    <label htmlFor="subject" className="form-label">
                      Subject<span className="required">*</span>
                    </label>
                    <CustomSelect
                      options={subjectOptions.map((opt) => opt.text)}
                      defaultIndex={subjectOptions.findIndex(
                        (opt) => opt.value === formData.subject,
                      )}
                      onTypeChange={handleSubjectChange}
                      title="Select Category"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="description" className="form-label">
                      Description <span className="required">*</span>
                    </label>
                    <textarea
                      id="description"
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      placeholder="Please provide detailed information about your issue..."
                      className="form-textarea"
                      rows="4"
                      required
                    />
                  </div>
                  {getCurrentSubject().uploadText && (
                    <div className="form-group">
                      <label htmlFor="fileUpload" className="form-label">
                        Upload Documents{" "}
                        {getCurrentSubject().uploadRequired && (
                          <span className="required">*</span>
                        )}
                      </label>
                      <p className="upload-info">
                        {getCurrentSubject().uploadText}
                      </p>
                      <div className="custom-file-input">
                        <input
                          type="file"
                          id="fileUpload"
                          multiple
                          accept=".jpg,.jpeg,.pdf,.png"
                          onChange={handleFileUpload}
                          className="file-input-hidden"
                        />
                        <label
                          htmlFor="fileUpload"
                          className="file-input-label"
                        >
                          Upload Files
                        </label>
                      </div>
                      <p className="file-requirements">
                        Accepted formats: JPG, JPEG, PNG, PDF | Max size: 5MB
                        per file
                      </p>

                      {uploadedFiles.length > 0 && (
                        <div className="uploaded-files">
                          <h4>Uploaded Files:</h4>
                          {uploadedFiles.map((file, index) => (
                            <div key={index} className="file-item">
                              <span className="file-name">{file.name}</span>
                              <span className="file-size">
                                ({(file.size / 1024 / 1024).toFixed(2)} MB)
                              </span>
                              <div onClick={() => removeFile(index)}>
                                <Cross
                                  size={19}
                                  color={COLOR_VARS["SWIFT_COLOR4"]}
                                />
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </form>
              </div>

              <div className="modal-foooter">
                <div
                  className="raise-ticket-button"
                  onClick={!isSubmitting ? handleSubmit : undefined}
                >
                  <CustomIconButton
                    disabled={isSubmitting}
                    title={"Raise Ticket"}
                    onClick={() => {}}
                    className={size === "S" ? "btn-xl" : ""}
                  />
                </div>
              </div>
            </div>
          </div>
        </SwiftModal>
      )}
      {isDetailsModalOpen && selectedTicket && (
        <SwiftModal closeModal={closeDetailsModal} top="2%">
          <div className="ticket-details-modal">
            <div className="modal-header-content">
              <div className="ticket-header-info">
                <div className="ticket-header-info-badge-close">
                  <div className="ticket-id-badge">
                    #{selectedTicket.ticket_id}
                  </div>
                  <div className="modal-close" onClick={closeDetailsModal}>
                    <Cross size={30} color={COLOR_VARS["SWIFT_COLOR7"]} />
                  </div>
                </div>
                <div className="ticket-meta">
                  <h3 className="ticket-subject">{selectedTicket.subject}</h3>
                </div>
              </div>
            </div>

            <div className="modal-wrapper">
              {/* Conversation Body */}
              <div className="modal-body">
                <div className="conversation-messages">
                  {sendingMessage ? (
                    <div className="conversation-loading">
                      <Pulse />
                      {/* <p>Sending message...</p> */}
                    </div>
                  ) : conversationLoading ||
                    (conversations.length === 0 &&
                      !conversationError &&
                      selectedTicket) ? (
                    <div className="conversation-loading">
                      <Pulse />
                      {/* <p>Loading conversation...</p> */}
                    </div>
                  ) : conversationError ? (
                    <p className="error-message">
                      <img src={fail} alt="Fail Image" />
                      {conversationError}
                    </p>
                  ) : conversations.length === 0 ? (
                    <div className="no-messages">
                      <p>No messages yet</p>
                      <p>Start the conversation by sending a message below.</p>
                    </div>
                  ) : (
                    <>
                      {conversations.map((msg, index) => (
                        <div key={index} className={`message ${msg.sender}`}>
                          <div className="message-card">
                            <div className="message-header">
                              {msg.sender !== "user" && (
                                <span className="sender-name">
                                  {msg.senderName}
                                </span>
                              )}
                              <span className="message-time">
                                {formatDate(msg.timestamp)}
                              </span>
                            </div>
                            <div
                              className="message-content"
                              dangerouslySetInnerHTML={{
                                __html: convertToHtml(msg.message),
                              }}
                            ></div>
                            {msg.fileUrls && msg.fileUrls.length > 0 && (
                              <div className="message-files">
                                {msg.fileUrls.map((file, fileIndex) => (
                                  <div key={fileIndex} className="message-file">
                                    <a
                                      href={file.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                    >
                                      {truncateFileName(file.originalName)}
                                    </a>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                      <div ref={messagesEndRef} />
                    </>
                  )}
                </div>
              </div>

              {/* Modal Footer - Message Input */}
              <div className="modal-footer">
                <div className="message-input-container">
                  <div className="message-input-container-input">
                    <textarea
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder="Type your message here..."
                      className="message-input"
                      rows="2"
                    />
                    {conversationFiles.length > 0 && (
                      <div className="uploaded-files">
                        {conversationFiles.map((file, index) => (
                          <div key={index} className="file-item">
                            <span className="file-name">{file.name}</span>
                            <span className="file-size">
                              ({(file.size / 1024 / 1024).toFixed(2)} MB)
                            </span>
                            <div
                              style={{ display: "flex", paddingRight: "2px" }}
                              onClick={() => removeConversationFile(index)}
                            >
                              <Cross
                                size={16}
                                color={COLOR_VARS["SWIFT_COLOR4"]}
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* File Upload for Conversation */}
                  <div className="conversation-file-upload">
                    <input
                      type="file"
                      multiple
                      accept=".jpg,.jpeg,.png,.gif,.pdf"
                      onChange={handleConversationFileChange}
                      style={{ display: "none" }}
                      id="conversation-file-input"
                    />

                    <div className="file-uploads-button">
                      <label
                        htmlFor="conversation-file-input"
                        className="file-upload-label"
                      >
                        <CustomIconButton
                          title="Upload Files"
                          // icon={
                          //   <Add size={20} color={COLOR_VARS["SWIFT_COLOR8"]} />
                          // }
                        />
                      </label>

                      <label>
                        <CustomIconButton
                          title="Send Message"
                          onClick={sendMessage}
                          disabled={
                            (!newMessage && conversationFiles.length === 0) ||
                            sendingMessage
                          }
                        />
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </SwiftModal>
      )}
    </div>
  );
}

export default RaiseTicket;
